import { useEffect, useMemo, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Printer, CheckCircle, ShieldCheck } from "lucide-react";
import DemoQRCode from "@/components/DemoQRCode";

function money(n) {
  if (!isFinite(n)) return "$0";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(Math.max(0, n));
}

export default function OnePagerPage() {
  const [s, setS] = useState(null);
  const [monthlyRos, setMonthlyRos] = useState(1000);
  const [avgDeclined, setAvgDeclined] = useState(150);
  const [recoveryPct, setRecoveryPct] = useState(10);

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data)).catch(() => {});
  }, []);

  const dealer = s?.dealership_name || "Your Dealership";

  const { monthly, annual } = useMemo(() => {
    const m = Number(monthlyRos) * Number(avgDeclined) * (Number(recoveryPct) / 100);
    return { monthly: m, annual: m * 12 };
  }, [monthlyRos, avgDeclined, recoveryPct]);

  return (
    <DashboardShell>
      <div className="mb-6 flex items-start justify-between gap-4 no-print">
        <div>
          <div className="font-mono-label text-muted-foreground">Sales Collateral</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">One-Pager Handout</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            The single sheet you leave on the service director&apos;s desk. Type their numbers,
            print, hand over. Every dollar on the page is calculated from what <i>they</i> just told you.
          </p>
        </div>
        <Button onClick={() => window.print()} data-testid="print-onepager-btn">
          <Printer className="w-4 h-4 mr-2" strokeWidth={1.75} />
          Print
        </Button>
      </div>

      {/* Calculator inputs — screen only, hidden from print */}
      <div className="max-w-[8.5in] mx-auto mb-4 no-print" data-testid="onepager-calc-inputs">
        <div className="font-mono-label text-muted-foreground text-[0.65rem] tracking-widest mb-2">
          Enter your dealership&apos;s numbers
        </div>
        <div className="rounded-lg border border-border bg-muted/40 p-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="op-ros" className="font-mono-label">Monthly RO&apos;s</Label>
            <Input
              id="op-ros"
              type="number"
              min={0}
              value={monthlyRos}
              onChange={(e) => setMonthlyRos(e.target.value)}
              className="mt-1.5 tabular-nums"
              data-testid="onepager-input-ros"
            />
          </div>
          <div>
            <Label htmlFor="op-avg" className="font-mono-label">Avg Declined $ / RO</Label>
            <Input
              id="op-avg"
              type="number"
              min={0}
              value={avgDeclined}
              onChange={(e) => setAvgDeclined(e.target.value)}
              className="mt-1.5 tabular-nums"
              data-testid="onepager-input-avg"
            />
          </div>
          <div>
            <Label htmlFor="op-pct" className="font-mono-label">Expected Recovery %</Label>
            <Input
              id="op-pct"
              type="number"
              min={0}
              max={100}
              value={recoveryPct}
              onChange={(e) => setRecoveryPct(e.target.value)}
              className="mt-1.5 tabular-nums"
              data-testid="onepager-input-pct"
            />
          </div>
        </div>
      </div>

      <div className="bg-white text-neutral-900 max-w-[8.5in] mx-auto shadow-2xl border border-border rounded-lg overflow-hidden" data-testid="onepager-sheet">
        <div className="bg-navy text-white px-10 py-8 flex items-center gap-6">
          <img
            src="/logo-brand.png"
            alt="Decline Drive"
            className="w-40 h-40 sm:w-48 sm:h-48 object-contain shrink-0"
            data-testid="onepager-logo"
          />
          <div>
            <div className="font-display font-black text-3xl sm:text-4xl tracking-tight">Decline Drive™</div>
            <div className="text-base sm:text-lg text-[hsl(var(--gold-light))] italic mt-1">We Drive Further</div>
          </div>
        </div>
        <div className="gold-divider" />

        <div className="px-10 py-8">
          <div className="mb-6">
            <div className="font-mono-label text-neutral-500 mb-2">Prepared for</div>
            <div className="font-display font-black text-2xl text-navy" data-testid="onepager-dealer-name">{dealer}</div>
          </div>

          <div className="font-display text-lg text-center text-neutral-800 mb-6 leading-snug">
            Here&apos;s what recovering your declines could look like
          </div>

          {/* Live estimate — pulled from the dealer's own numbers */}
          <div className="grid grid-cols-2 gap-6 mb-4">
            <div className="bg-emerald-50 border-2 border-emerald-600 rounded-lg p-6 text-center overflow-hidden" data-testid="onepager-monthly-card">
              <div className="font-mono-label text-emerald-700 text-xs">Monthly Recovered $</div>
              <div className="font-display font-black text-2xl md:text-3xl text-emerald-700 tabular-nums mt-2 break-words">
                {money(monthly)}
              </div>
              <div className="text-xs text-neutral-600 mt-1">estimated per month</div>
            </div>
            <div className="bg-amber-50 border-2 border-amber-600 rounded-lg p-6 text-center overflow-hidden" data-testid="onepager-annual-card">
              <div className="font-mono-label text-amber-700 text-xs">Annual Recovered $</div>
              <div className="font-display font-black text-2xl md:text-3xl text-amber-700 tabular-nums mt-2 break-words">
                {money(annual)}
              </div>
              <div className="text-xs text-neutral-600 mt-1">estimated per year</div>
            </div>
          </div>

          <div className="bg-neutral-100 border-l-4 border-[hsl(var(--gold))] rounded-r-lg px-4 py-3 mb-6" data-testid="onepager-estimate-note">
            <div className="font-mono-label text-neutral-500 text-[0.6rem] tracking-widest">ESTIMATE BASED ON YOUR NUMBERS</div>
            <div className="text-sm text-neutral-800 mt-0.5 leading-snug">
              <b className="text-navy tabular-nums">{Number(monthlyRos || 0).toLocaleString()}</b> RO&apos;s / month ×{" "}
              <b className="text-navy">{money(Number(avgDeclined || 0))}</b> average declined per RO ×{" "}
              <b className="text-navy">{Number(recoveryPct || 0)}%</b> recovery.
              Edit the numbers above to fit this dealership.
            </div>
          </div>

          <div className="text-center mb-6" data-testid="onepager-automated-label">
            <div className="font-mono-label text-neutral-500 text-[0.65rem] tracking-[0.35em]">100% AUTOMATED</div>
            <div className="text-sm text-neutral-700 mt-1">
              Nightly DMS pull → texts fire in your quiet-hours window → customer taps to book. Zero clicks from your team.
            </div>
          </div>

          <div className="space-y-3 mb-6">
            {[
              s?.stage_1_days && s?.stage_2_days
                ? `Every declined-service customer texted automatically — on your saved schedule (Day ${s.stage_1_days} at ${s.stage_1_discount}% off, Day ${s.stage_2_days} at ${s.stage_2_discount}% off)`
                : "Every declined-service customer texted automatically — on the schedule you set in Settings",
              "Unique trackable coupon code per customer — every recovered RO auditable",
              "Nightly DMS data pull — your staff never uploads a report or presses a button",
              "Live dashboard shows every advisor's recovery scorecard in real time",
              "White-glove onboarding included — you are texting customers by Day 1",
            ].map((line, i) => (
              <div key={i} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" strokeWidth={2} />
                <div className="text-sm text-neutral-800 leading-relaxed">{line}</div>
              </div>
            ))}
          </div>

          {/* 60-Day Guarantee */}
          <div className="rounded-lg bg-navy text-white p-5 flex items-center gap-4 mb-6" data-testid="onepager-guarantee">
            <div className="w-14 h-14 rounded-full bg-[hsl(var(--gold))] grid place-items-center shrink-0">
              <ShieldCheck className="w-8 h-8 text-neutral-900" strokeWidth={2} />
            </div>
            <div>
              <div className="font-display font-black text-lg tracking-tight text-[hsl(var(--gold-light))]">
                60-Day Guarantee
              </div>
              <div className="text-sm text-white/90 mt-0.5">
                Run it for 60 days. If Decline Drive doesn&apos;t pay for itself in recovered RO&apos;s,
                you don&apos;t owe another dollar.
              </div>
            </div>
          </div>

          <div className="text-center py-4 border-t-2 border-neutral-200">
            <div className="font-mono-label text-neutral-500 text-xs mb-1">Investment</div>
            <div className="font-display text-2xl font-bold text-neutral-800">
              ${(s?.monthly_fee ?? 1295).toLocaleString()}/month
              <span className="text-sm text-neutral-500 font-normal ml-2">+ ${(s?.onboarding_fee ?? 895).toLocaleString()} one-time</span>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-neutral-200">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-center sm:text-left">
                <div className="font-display font-bold text-base">{s?.owner_name || "James Ayers"}</div>
                <div className="text-xs text-neutral-600">
                  {s?.owner_email || "jayers.me@gmail.com"}
                  {s?.owner_phone ? ` · ${s.owner_phone}` : " · (913) 408-7100"}
                </div>
              </div>
              <DemoQRCode size={100} caption="Scan · see the app in action" />
            </div>
            <div className="text-[0.6rem] text-neutral-500 mt-3 text-center">
              © {new Date().getFullYear()} Decline Drive™ by Ayers Enterprises. All rights reserved.
            </div>
          </div>
        </div>
      </div>
    </DashboardShell>
  );
}
